import React, { ChangeEvent, useCallback } from 'react';

import { Box, GridLegacy as Grid, TextField, Typography } from '@mui/material';

import type { BundleCabinMarkupFormRow } from '~/components/Cruises/pages/Bundles/types';

type Props = {
  rows: Array<BundleCabinMarkupFormRow>;
  onChange: (rows: Array<BundleCabinMarkupFormRow>) => void;
  disabled?: boolean;
};

function parseNumericInput(value: string): number | '' {
  if (value === '') {
    return '';
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? '' : parsed;
}

function BundleCabinTypeMarkupsEditor({ rows, onChange, disabled }: Props) {
  const handleMarkupChange = useCallback(
    (cabinType: string, markupPercent: number | '') => {
      onChange(rows.map((row) => (row.cabinType === cabinType ? { ...row, markupPercent } : row)));
    },
    [rows, onChange],
  );

  const handleFlightCreditChange = useCallback(
    (cabinType: string, flightCreditAmount: number | '') => {
      onChange(rows.map((row) => (row.cabinType === cabinType ? { ...row, flightCreditAmount } : row)));
    },
    [rows, onChange],
  );

  return (
    <Box mt={2}>
      <Typography variant="subtitle2" gutterBottom>
        Cabin type markups
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 'lg' }}>
        Markup NET price by cabin type (0–100%, stored as decimals 0–1). Flight credit is per person in AUD/NZD and is
        added to the deposit at checkout. All four cabin types are required.
      </Typography>
      {rows.map((row) => (
        <Grid key={row.cabinType} container spacing={2} alignItems="flex-start" sx={{ mb: 1.5 }}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Cabin type" value={row.label} disabled />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              required
              fullWidth
              label="Markup (%)"
              type="number"
              value={row.markupPercent}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                handleMarkupChange(row.cabinType, parseNumericInput(event.target.value));
              }}
              disabled={disabled}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              required
              fullWidth
              label="Flight credit ($ per person)"
              type="number"
              value={row.flightCreditAmount}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                handleFlightCreditChange(row.cabinType, parseNumericInput(event.target.value));
              }}
              disabled={disabled}
              inputProps={{ min: 0, step: 1 }}
              helperText="Enter 0 if no flight credit"
            />
          </Grid>
        </Grid>
      ))}
    </Box>
  );
}

export default BundleCabinTypeMarkupsEditor;
